import { FITNESS_DOMAINS, type FitnessDomain } from '~/models/fitnessDomain'
import type { DomainRulesUnavailable } from '~/models/rulesOpinion'
import type { IsoTimestamp } from '~/types/dates'
import { RULES_ENGINE_VERSION } from './version'

/**
 * Which domains this build can actually reason about (D-090, extending D-043).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE SAME SHAPE AS `RULESET_REGISTRY`, AND FOR THE SAME REASON
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  A total map over `FitnessDomain`, so adding a fifth domain without deciding
 *  whether it can be judged is a compile error rather than a silent fallback.
 *  **There is no default branch anywhere in this file.** A domain with no rules
 *  does not borrow another domain's logic; it gets a terminal
 *  `DomainRulesUnavailable`, which is never a recommendation.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  ONE DIFFERENCE FROM THE PHASE REGISTRY, AND IT MATTERS
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  An unimplemented phase is unreachable in ordinary use — `SELECTABLE_PHASE_TYPES`
 *  is derived from the registry, so a user cannot choose one. **An unimplemented
 *  domain is entirely ordinary**: the user picks cardio because they want to work
 *  on cardio, and no cardio rules have been approved. So this registry does *not*
 *  gate selection, and there is deliberately no `SELECTABLE_DOMAINS`. Hiding
 *  cardio until its rules existed would be refusing to track something the user
 *  can perfectly well track.
 *
 *  What it gates is **judgement**. Project Sola records every active domain and
 *  says out loud which ones it is not yet forming an opinion about.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  WHY NOTHING BUT BODY COMPOSITION IS IMPLEMENTED
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  D-060: no rule may be implemented whose evidence the app does not gather, and
 *  no threshold may be invented to make one possible. Cardio has no approved
 *  progression thresholds and no wearable import yet (C8.x); mobility has no
 *  published or conventional basis anyone could cite; strength as an *improvement
 *  domain* has neither, and must not be confused with the strength guardrail that
 *  already runs inside the body-composition opinion.
 *
 *  Flipping one of these to `true` requires the same thing every phase required:
 *  approved thresholds with their provenance in `rules/thresholds.ts`, a ruleset,
 *  and its matrix passing. Not a code change on its own.
 */

export interface DomainRegistryEntry {
  readonly implemented: boolean
  /**
   * Why not, in the user's words. Present exactly when `implemented` is false.
   *
   * Stored on the entry rather than composed at the call site so that every
   * screen and every export gives the same reason — and so that the reason has to
   * be written at the moment someone decides a domain cannot be judged.
   */
  readonly reason?: string
}

export const DOMAIN_REGISTRY: Readonly<Record<FitnessDomain, DomainRegistryEntry>> = {
  bodyComposition: { implemented: true },
  cardio: {
    implemented: false,
    reason:
      'Project Sola records your cardio but does not judge it yet. It needs a baseline to compare against, and the rules for reading one have not been approved.',
  },
  strength: {
    implemented: false,
    reason:
      'Project Sola records your lifts and already watches them for a decline while you are in a phase. It does not yet plan strength as a goal of its own.',
  },
  mobility: {
    implemented: false,
    reason:
      'Project Sola records your mobility sessions and shows them back. It does not judge them, because there is no measure it could defend.',
  },
}

/** The domains this build forms an opinion about. Derived, never a second list. */
export const JUDGEABLE_DOMAINS: readonly FitnessDomain[] = FITNESS_DOMAINS.filter(
  (domain) => DOMAIN_REGISTRY[domain].implemented,
)

export function isDomainImplemented(domain: FitnessDomain): boolean {
  return DOMAIN_REGISTRY[domain].implemented
}

/**
 * The terminal outcome for a domain with no rules.
 *
 * Built here so the reason can only come from the registry. A caller composing
 * its own sentence is how two screens end up explaining the same absence
 * differently, and how one of them ends up sounding like a failure.
 */
export function domainRulesUnavailable(
  domain: FitnessDomain,
  evaluatedAt: IsoTimestamp,
): DomainRulesUnavailable {
  const entry = DOMAIN_REGISTRY[domain]
  return {
    outcome: 'domainRulesUnavailable',
    domain,
    /* The fallback is unreachable while the registry requires a reason for every
       unimplemented domain, and it is here rather than as a non-null assertion
       because a blank explanation on screen would be worse than a general one. */
    reason: entry.reason ?? 'Project Sola does not judge this area yet.',
    engineVersion: RULES_ENGINE_VERSION,
    evaluatedAt,
  }
}
